import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Fab,
  Grid,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  Stepper,
  Step,
  StepLabel,
  Pagination,
  AccordionDetails,
  Chip,
  Link,
} from '@mui/material';
import api from '../../api/api';
import dayjs from 'dayjs';
import * as XLSX from "xlsx";

import ApproveRequestDialogManager from '../commonservices/ApproveRequestDialogManager';
import ApproveRequestDialogAdmin from '../commonservices/ApproveRequestDialogAdmin';

import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../contexts/AuthContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import SourceIcon from '@mui/icons-material/Source';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Person2Icon from '@mui/icons-material/Person2';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import InfoIcon from '@mui/icons-material/Info';
import TimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import DateRangeIcon from '@mui/icons-material/DateRange';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import DescriptionIcon from '@mui/icons-material/Description';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';

const PAGE_SIZE = 5;

const HotelBookingListingPage = () => {
    var utc = require("dayjs/plugin/utc");
    dayjs.extend(utc);
  
    const navigate = useNavigate();
    const { user } = useAuth();
    const [userId, setUserId] = useState(null);
    const [role, setRole] = useState(null);
    const [page, setPage] = useState(1);
  
    const handlePageChange = (event, value) => {
      setPage(value);
    }
  
  
    useEffect(() => {
      if (user) {
        setUserId(user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']);
        setRole(user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
      }
    }, [user]);
  
    const [hotelBookingRequest, setHotelBookingRequest] = useState([]);
  
    const [filteredData, paginatedData] = useMemo(() => {
      const filteredData = hotelBookingRequest?.filter(
        req => req.employeeId == userId ||
          (role == "Manager" && req.approvalManagerId == userId) ||
          (role == "Admin" && req.managerApprovedAt)
      );
      return [filteredData, filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)];
    }, [userId, role, page, hotelBookingRequest])
  
  
    // APIs
    const loadHotelBookingRequest = () => {
      api.get('/api/HotelBookingRequest')
        .then(response => {
          setHotelBookingRequest(response.data);
        })
        .catch(error => {
          console.log('Error fetching Hotel Booking List', error);
        });
    }
  
    useEffect(() => {
      loadHotelBookingRequest();
    }, []);
  
    const getFileIcon = (file) => {
      const extension = file.name.split('.').pop().toLowerCase();
      switch (extension) {
        case 'jpg':
          break;
        case 'jpeg':
          break;
        case 'png':
          break;
      }
    };
  
    //#region  Excel section
    const handleDownload = async (e) => {
      let wb = XLSX.utils.book_new(),
        pws = XLSX.utils.json_to_sheet([]);
  
      let hotelBookingRequestHeaders = [[
        "Request Id",
        "Requester",
        "Requested At",
        "Location",
        "Number of Person",
        "Number of Rooms",
        "Check In Date",
        "Check In Time",
        "Check Out Date",
        "Check Out Time",
        "Approval Manager",
        "Manager Approved At",
        "Manager Rejected At",
        "Manager Remarks",
        "Admin Approved At",
        "Admin Remarks",
        "Cancelled By",
        "Cancelled At",
        "Completed At"]];
  
      const hotelBookingRequestData = filteredData?.map((c) => ({
        RequestId: c.requestId,
        Requester: c.employee?.name,
        RequestedAt: dayjs(c.createdAt).format("DD/MM/YYYY"),
        Location: c.location,
        numberOfPerson: c.noOfPerson,
        numberOfRooms: c.noOfRooms,
        CheckInDate: c.checkInDate && dayjs(c.checkInDate).format("DD/MM/YYYY"),
        CheckInTime: c.checkInTime && dayjs(c.checkInTime).format("hh:mm a"),
        CheckOutDate: c.checkOutDate && dayjs(c.checkOutDate).format("DD/MM/YYYY"),
        CheckOutTime: c.checkOutTime && dayjs(c.checkOutTime).format("hh:mm a"),
        ApprovalManager: c.approvalManager?.name,
        ManagerApprovedAt: c.managerApprovedAt && dayjs(c.managerApprovedAt).format("DD/MM/YYYY"),
        ManagerRejectedAt: c.managerRejectedAt && dayjs(c.managerRejectedAt).format("DD/MM/YYYY"),
        ManagerRemarks: c.managerRemarks,
        AdminApprovedAt: c.adminApprovedAt && dayjs(c.adminApprovedAt).format("DD/MM/YYYY"),
        AdminRemarks: c.adminRemarks,
        CancelledBy: c.cancelledBy?.name,
        CancelledAt: c.CancelledAt && dayjs(c.CancelledAt).format("DD/MM/YYYY"),
        CompletedAt: c.completedAt && dayjs(c.completedAt).format("DD/MM/YYYY")
      }));
  
      XLSX.utils.sheet_add_aoa(pws, hotelBookingRequestHeaders);
      XLSX.utils.sheet_add_json(pws, hotelBookingRequestData, { origin: 'A2', skipHeader: true });
  
      XLSX.utils.book_append_sheet(wb, pws, "Hotel Booking Requests");
  
      XLSX.writeFile(wb, "Export.xlsx");
    }
    //#endregion
  
    //#region Manager Approval Section
  
    const [dialogOpen, setDialogOpen] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
  
    const handleRejectOpenDialog = (request) => {
      setSelectedRequest(request);
      setDialogOpen('reject');
    };
  
    const handleCancelRequest = (request) => {
  
      request.cancelledAt = dayjs().utc('z').format();
      request.cancelledById = userId;
  
      api.put(`/api/HotelBookingRequest/${request.id}`, request)
        .then(response => {
          loadHotelBookingRequest();
          handleCloseDialog();
        })
        .catch(error => {
          console.error('Error cancelling request', error);
          handleCloseDialog();
          // Handle error
        });
    };
  
    const handleCompleteRequest = (request) => {
  
      request.completedAt = dayjs().utc('z').format();
  
      api.put(`/api/HotelBookingRequest/${request.id}`, request)
        .then(response => {
          loadHotelBookingRequest();
          handleCloseDialog();
        })
        .catch(error => {
          console.error('Error submitting request', error);
          handleCloseDialog();
          // Handle error
        });
    };
  
    const handleCloseDialog = () => {
      setDialogOpen(null);
      setSelectedRequest(null);
    };
  
    const handleApproveOpenDialog = (request) => {
      setSelectedRequest(request);
      setDialogOpen('approve');
    };
  
    const handleApproveManager = (remark) => {
  
      if (dialogOpen === 'approve') {
        selectedRequest.managerApprovedAt = dayjs().utc('z').format();
      } else {
        selectedRequest.managerRejectedAt = dayjs().utc('z').format();
      }
  
      selectedRequest.managerRemarks = remark;
  
      api.put(`/api/HotelBookingRequest/${selectedRequest.id}`, selectedRequest)
        .then(response => {
          loadHotelBookingRequest();
          handleCloseDialog();
        })
        .catch(error => {
          console.error('Error approving request', error);
          handleCloseDialog();
          // Handle error
        });
    };
  
    const handleApproveAdmin = (remark, files) => {
      const formData = new FormData();
      
      // Append only required fields
      formData.append("id", selectedRequest.id);
      selectedRequest.adminApprovedAt = dayjs().utc('z').format();
      selectedRequest.adminRemarks = remark;
  
      formData.append("adminApprovedAt", selectedRequest.adminApprovedAt);
      formData.append("adminRemarks", selectedRequest.adminRemarks);
      // Append files if they exist
      files.forEach((file, index) => {
        formData.append(`files`, file);
      });
  
      formData.forEach((value, key) => {
        console.log(`${key}:`, value);
    });
    
  
      // Send the request
      api.put('/api/HotelBookingRequest/UpdateHotelBookingInfo', formData, {
          headers: {
              "Content-Type": "multipart/form-data",
          },
      })
      .then(response => {
          console.log("Request approved successfully", response);
          loadHotelBookingRequest();
          handleCloseDialog();
      })
      .catch(error => {
          console.error("Error approving request", error);
          handleCloseDialog();
      });
  };
  
    
    //#endregion
  
    return (
      <Box sx={{ p: 2 }}>
        <Box mb={2} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Typography component="p" variant="h5">Hotel Booking Requirement</Typography>
          {
            <Button size='small' variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Excel
            </Button>
          }
        </Box>
        <Grid container spacing={2}>
          {paginatedData?.map((request) => (
            <Grid item xs={12} key={request.id}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${request.id}-content`}
                  id={`panel${request.id}-header`}
                >
                  <Box sx={{
                    width: '100%', display: 'grid'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <b>Requester:</b> {request.employee.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SourceIcon sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <b>Request Id:</b> {request.requestId}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MyLocationIcon sx={{ mr: 1 }} />
                      <Typography variant="body1"
                        sx={{
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          WebkitLineClamp: 2,
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal'
                        }}
                      >
                        <b>Location:</b> {request.location}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person2Icon sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <b>Manager:</b> {request.approvalManager?.name}
                      </Typography>
                    </Box>
                    {request.cancelledBy &&
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon color='warning' sx={{ mr: 1 }} />
                        <Typography variant="body1">
                          <b>Cancelled By:</b> {request.cancelledBy.name}
                        </Typography>
                      </Box>
                    }
                    <Box sx={{ width: '100%', overflowX: 'auto', display: 'block' }}>
                      <Stepper activeStep={request.adminApprovedAt || request.adminRejectedAt ? 3 : request.managerApprovedAt ? 2 : 1}>
                        <Step key={'request'} >
                          <StepLabel>Request</StepLabel>
                        </Step>
                        <Step key={'manager'}>
                          <StepLabel {...{ error: (request.managerRejectedAt != null) }}>Manager</StepLabel>
                        </Step>
                        <Step key={'admin'}>
                          <StepLabel {...{ error: (request.adminRejectedAt != null) }}>Admin</StepLabel>
                        </Step>
                      </Stepper>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Number of Person: </b>
                        {request.noOfPerson}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Number of Rooms: </b>
                        {request.noOfRooms}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DateRangeIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Check In Date:</b> {dayjs(request.checkInDate).format("DD/MM/YYYY")}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimeIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Check In Time:</b> {dayjs(request.checkInTime).format("hh:mm a")}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DateRangeIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Check Out Date:</b> {dayjs(request.checkOutDate).format("DD/MM/YYYY")}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimeIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Check Out Time:</b> {dayjs(request.checkOutTime).format("hh:mm a")}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DateRangeIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Requested At:</b> {dayjs(request.createdAt).format("DD/MM/YYYY")}</Typography>
                    </Box>
                    {request.adminRemarks &&
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {request.adminApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                        <Typography variant="body2"><b>Admin Remarks:</b> {request.adminRemarks}</Typography>
                      </Box>
                    }
                    {request.managerRemarks &&
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {request.managerApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                        <Typography variant="body2"><b>Manager Remark:</b> {request.managerRemarks}</Typography>
                      </Box>
                    }
                    <Grid container rowSpacing={1} columnSpacing={1}>
                      {request.hotelBookingDocuments?.map((file, index) => (
                        <Grid item key={index}>
                          <Link download={file.document.name} target="_blank" rel="noopener" href={`${process.env.REACT_APP_BASE_URL}/${file.document.path}`}>
                            <Chip
                              icon={getFileIcon(file.document)}
                              label={file.document.name}
                            />
                          </Link>
                        </Grid>
                      ))}
                    </Grid>
                    {
                      request.completedAt == null &&
                      <>
                        {((role == "Admin" && !request.adminApprovedAt && !request.adminRejectedAt) 
                          || (role == "Manager" && !request.managerApprovedAt && !request.managerRejectedAt)) 
                          &&
                          <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, mt: 1 }}>
                            <Button onClick={() => handleApproveOpenDialog(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                              Approve
                            </Button>
                            {role != "Admin" 
                              && 
                              <Button onClick={() => handleRejectOpenDialog(request)} variant='contained' color='error' sx={{ flex: '1 1 auto' }} >
                                Reject
                              </Button>
                            }
                          </Box>
                        }
                        <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, mt: 1 }}>
                          {role != "Admin" && !request.managerRejectedAt && !request.cancelledById 
                            && 
                            <Button fullWidth onClick={() => handleCancelRequest(request)} variant='contained' color='warning' sx={{ flex: '1 1 auto' }} >
                              Cancel Request
                            </Button>
                          }
                          {role == "Requester" && !request.managerRejectedAt && !request.cancelledById 
                            && 
                            <Button fullWidth onClick={() => handleCompleteRequest(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                              Complete
                            </Button>
                          }
                        </Box>
                      </>
                    }
  
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 6 }}>
          <Pagination
            count={Math.ceil(filteredData.length / PAGE_SIZE)}
            page={page}
            siblingCount={1}
            boundaryCount={1}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
        <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: '2rem', right: '2rem' }} onClick={() => navigate('/hotel-request/add')}>
          <AddIcon />
        </Fab>
        {role == "Manager" &&
          <ApproveRequestDialogManager open={dialogOpen != null} isApprove={dialogOpen === 'approve'} handleClose={handleCloseDialog} handleApprove={handleApproveManager} />
        }
        {role == "Admin" &&
          <ApproveRequestDialogAdmin open={dialogOpen != null} handleClose={handleCloseDialog} handleApprove={handleApproveAdmin} moduleName={"HotelBooking"} />
        }
      </Box>
    )
}

export default HotelBookingListingPage